/**
 * Viewport and shell flags of the approval page: a `matchMedia` hook, the `lg` breakpoint and the route's
 * `hideBottomBar` flag (the decision bar takes the shell's phone bar while the approval is pending).
 */
import { useQuery } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';
import { approvalQuery } from '../../app/queries.ts';

// -------------------------------------------------------------------------------------------------
// Breakpoints and the shell flag
// -------------------------------------------------------------------------------------------------

/** True while the viewport is at least `px` wide (`false` where `matchMedia` is missing). */
export function useMinWidth(px: number): boolean {
  const query = `(min-width: ${px}px)`;
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false),
    () => false,
  );
}

/** Tailwind `lg` in px. */
export const LG = 1024;

/**
 * The approval route's `hideBottomBar` flag (read by the shell): the shell hides its phone bottom bar while the
 * approval is pending, because the decision bar takes its place. After a decision the shell bar returns.
 */
export function useApprovalHidesBottomBar(params: Record<string, string>): boolean {
  const approvalId = params.approvalId ?? '';
  const detail = useQuery({
    ...approvalQuery(approvalId),
    enabled: approvalId !== '',
    meta: { silent: true },
  });
  return detail.data?.approval.status === 'pending';
}
